import { IPublicClientApplication, LogLevel, PublicClientApplication } from "@azure/msal-browser";

import { AzureAuthConfiguration } from "../azure-auth.types";

let azureAuthInstance: IPublicClientApplication;

/**
 * Async function that creates an instance of the Azure Active Directory client from the passed configuration
 * and initializes it. The instance initialization must resolve before invoking other AzureAuthAuth APIs, it's then used
 * as a property of the AzureAuthAuthenticationProvider component.
 * @param configuration The configuration for the Azure Active Directory client.
 * @returns The instance of the Azure Active Directory client.
 */
const createAzureInstance = async (configuration: AzureAuthConfiguration) => {
    const {
        authorityUrl,
        clientId,
        redirectUrl,
        postLogoutRedirectUrl,
        knownAuthorities = configuration.authorityUrl ? [configuration.authorityUrl] : [],
        logLevel = LogLevel.Error,
    } = configuration;

    azureAuthInstance = new PublicClientApplication({
        auth: {
            authority: authorityUrl,
            clientId: clientId,
            redirectUri: redirectUrl,
            postLogoutRedirectUri: postLogoutRedirectUrl,
            knownAuthorities: knownAuthorities,
        },
        cache: {
            cacheLocation: "sessionStorage",
            secureCookies: true,
        },
        system: {
            loggerOptions: {
                logLevel: logLevel,
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Info:
                            // eslint-disable-next-line no-console
                            console.info(message);
                            return;
                        case LogLevel.Verbose:
                            // eslint-disable-next-line no-console
                            console.debug(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                        default:
                            // eslint-disable-next-line no-console
                            console.log(message);
                            return;
                    }
                },
            },
        },
    });

    await azureAuthInstance.initialize();

    return azureAuthInstance;
};

/**
 * Use this function to get the Azure Auth instance outside of React components,
 * otherwise use the `useAzureAuth` hook instead.
 */
export const getOrCreateAzureAuthInstance = async (
    configuration?: AzureAuthConfiguration
): Promise<IPublicClientApplication> => {
    if (azureAuthInstance) {
        return azureAuthInstance;
    } else if (configuration) {
        return await createAzureInstance(configuration);
    } else {
        throw new Error("Azure AD instance is not initialized. Provide a configuration to create a new instance.");
    }
};

/**
 * Use this function to get the Azure Auth instance outside of React components,
 * otherwise use the `useAzureAuth` hook instead.
 *
 * @deprecated The function was renamed to {@link getOrCreateAzureAuthInstance}.
 */
export const getAzureAuthInstance = async (
    configuration?: AzureAuthConfiguration
): Promise<IPublicClientApplication> => {
    return getOrCreateAzureAuthInstance(configuration);
};

/**
 * Use this function to get the Azure Auth instance outside of React components,
 * otherwise use the `useAzureAuth` hook instead.
 */
export const tryGetAzureAuthInstance = (): IPublicClientApplication => {
    if (azureAuthInstance) {
        return azureAuthInstance;
    } else {
        throw new Error("Azure AD instance is not initialized.");
    }
};
